/**
 * Recently Visited Products Renderer
 * Fetches product handles from localStorage and renders using card-product snippet via Shopify sections API
 */
(function() {
  'use strict';

  class RecentlyVisitedProductsRenderer {
    constructor() {
      this.storageKey = 'shopify_recently_viewed_products';
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initSections());
      } else {
        this.initSections();
      }

      // Re-initialize when sections are loaded dynamically
      document.addEventListener('shopify:section:load', () => this.initSections());
    }

    initSections() {
      document.querySelectorAll('[id^="recently-visited-"]').forEach(section => {
        this.loadAndRenderProducts(section);
      });
    }

    getCurrentProduct() {
      const productInfo = document.querySelector('product-info[data-product-id]');
      if (productInfo) {
        return {
          id: productInfo.dataset.productId,
          handle: productInfo.dataset.url?.match(/\/products\/([^\/\?#]+)/)?.[1] || ''
        };
      }
      
      const urlMatch = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
      if (urlMatch) {
        return { handle: urlMatch[1] };
      }
      
      return null;
    }

    getRecentlyViewedProducts() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (!stored) return [];
        
        const products = JSON.parse(stored);
        const currentProduct = this.getCurrentProduct();
        
        // Filter out current product if on product page
        if (currentProduct) {
          return products.filter(p => {
            if (currentProduct.id && p.id) {
              return p.id !== currentProduct.id;
            }
            if (currentProduct.handle && p.handle) {
              return p.handle !== currentProduct.handle;
            }
            return true;
          });
        }
        
        return products;
      } catch (e) {
        console.error('Error reading recently viewed products:', e);
        return [];
      }
    }

    async loadAndRenderProducts(section) {
      const recentlyViewed = this.getRecentlyViewedProducts();
      const productsToShow = parseInt(section.dataset.productsToShow) || 4;
      const sectionId = section.dataset.sectionId;

      if (recentlyViewed.length === 0) {
        this.showEmptyState(section);
        return;
      }

      // Get product handles (unique)
      const productHandles = recentlyViewed
        .map(p => p.handle)
        .filter(Boolean)
        .filter((handle, index, self) => self.indexOf(handle) === index)
        .slice(0, productsToShow);

      if (productHandles.length === 0) {
        this.showEmptyState(section);
        return;
      }

      // Render products using Shopify sections API to fetch card-product snippets
      await this.renderProducts(section, productHandles, sectionId);
    }

    async renderProducts(section, productHandles, sectionId) {
      const container = section.querySelector('[id^="recently-visited-products-"]');
      if (!container) return;

      const productList = container.querySelector('ul.grid');
      if (!productList) return;

      // Get section settings from data attributes
      const settings = {
        sectionId: sectionId,
        imageRatio: section.dataset.imageRatio || 'adapt',
        imageShape: section.dataset.imageShape || 'default',
        showSecondaryImage: section.dataset.showSecondaryImage === 'true',
        showVendor: section.dataset.showVendor === 'true',
        showRating: section.dataset.showRating === 'true',
        quickAdd: section.dataset.quickAdd || 'none',
        columnsDesktop: parseInt(section.dataset.columnsDesktop) || 4,
        columnsMobile: parseInt(section.dataset.columnsMobile) || 2
      };

      // Clear existing products
      const existingProducts = productList.querySelectorAll('li:not(.recently-visited-empty)');
      existingProducts.forEach(el => el.remove());

      // Hide empty state
      const emptyState = productList.querySelector('.recently-visited-empty');
      if (emptyState) {
        emptyState.style.display = 'none';
      }

      // Render each product by fetching rendered card-product snippet
      for (let index = 0; index < productHandles.length; index++) {
        const handle = productHandles[index];
        const listItem = await this.fetchProductCardHTML(handle, settings, index);
        if (listItem) {
          productList.appendChild(listItem);
        }
      }

      // Update slider counter
      const sliderCounter = container.querySelector('.slider-counter--total');
      if (sliderCounter) {
        sliderCounter.textContent = productHandles.length;
      }

      // Initialize card functionality
      this.initializeCardFunctionality(container);
    }

    async fetchProductCardHTML(handle, settings, index) {
      try {
        // First, fetch product JSON to get product ID
        const productResponse = await fetch(`/products/${handle}.js`);
        if (!productResponse.ok) {
          console.warn(`Product ${handle} not found`);
          return null;
        }
        const productData = await productResponse.json();
        const productId = productData.id?.toString() || '';

        // Use Shopify's sections API to fetch related products section
        // This section should contain card-product snippets
        const sectionsResponse = await fetch(`/products/${handle}?sections=related-products,product-recommendations`);
        const sections = await sectionsResponse.json();
        
        // Try related-products section first
        let cardHTML = sections['related-products'] || sections['product-recommendations'];
        
        if (cardHTML) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(cardHTML, 'text/html');
          
          // Find card-wrapper for this specific product
          const allCardWrappers = doc.querySelectorAll('.card-wrapper');
          let matchingCard = null;
          
          for (const card of allCardWrappers) {
            const cardLink = card.querySelector('a[href*="/products/"]');
            if (cardLink && (cardLink.href.includes(`/products/${handle}`) || cardLink.href.includes(`variant=${productId}`))) {
              matchingCard = card;
              break;
            }
          }
          
          // If not found by URL, try by product ID
          if (!matchingCard) {
            for (const card of allCardWrappers) {
              const cardProductId = card.querySelector('[data-product-id]')?.dataset.productId;
              if (cardProductId === productId) {
                matchingCard = card;
                break;
              }
            }
          }
          
          // If still not found, use the first card-wrapper (might be from a different product in related)
          if (!matchingCard && allCardWrappers.length > 0) {
            // Try to find card by checking the parent li's data attributes
            for (const card of allCardWrappers) {
              const parentLi = card.closest('li');
              if (parentLi) {
                const liHandle = parentLi.dataset.productHandle;
                const liProductId = parentLi.dataset.productId;
                if (liHandle === handle || liProductId === productId) {
                  matchingCard = card;
                  break;
                }
              }
            }
          }
          
          if (matchingCard) {
            const li = document.createElement('li');
            li.className = 'grid__item slider__slide';
            li.id = `Slide-${settings.sectionId}-${index + 1}`;
            li.setAttribute('data-product-handle', handle);
            if (productId) {
              li.setAttribute('data-product-id', productId);
            }
            
            // Get the parent li if it exists, otherwise just use the card-wrapper
            const parentLi = matchingCard.closest('li.grid__item');
            if (parentLi) {
              li.innerHTML = parentLi.innerHTML;
            } else {
              li.innerHTML = matchingCard.outerHTML;
            }
            
            return li;
          }
        }
        
        // Fallback: Fetch full product page and extract card-product
        const pageResponse = await fetch(`/products/${handle}`);
        const pageHtml = await pageResponse.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(pageHtml, 'text/html');
        
        const allCardWrappers = doc.querySelectorAll('.card-wrapper');
        for (const card of allCardWrappers) {
          const cardLink = card.querySelector('a[href*="/products/"]');
          if (cardLink && cardLink.href.includes(`/products/${handle}`)) {
            const li = document.createElement('li');
            li.className = 'grid__item slider__slide';
            li.id = `Slide-${settings.sectionId}-${index + 1}`;
            li.setAttribute('data-product-handle', handle);
            if (productId) {
              li.setAttribute('data-product-id', productId);
            }
            li.innerHTML = card.outerHTML;
            return li;
          }
        }
        
        console.warn(`Could not find card-product HTML for ${handle}`);
        return null;
      } catch (e) {
        console.error(`Error fetching card HTML for ${handle}:`, e);
        return null;
      }
    }


    showEmptyState(section) {
      const container = section.querySelector('[id^="recently-visited-products-"]');
      if (!container) return;

      const productList = container.querySelector('ul.grid');
      if (productList) {
        const emptyState = productList.querySelector('.recently-visited-empty');
        if (emptyState) {
          emptyState.style.display = 'block';
        }
      }

      const sliderCounter = container.querySelector('.slider-counter--total');
      if (sliderCounter) {
        sliderCounter.textContent = '0';
      }
    }

    initializeCardFunctionality(container) {
      // Trigger card initialization events
      const cards = container.querySelectorAll('.card-wrapper');
      cards.forEach(card => {
        card.dispatchEvent(new CustomEvent('card:loaded', { bubbles: true }));
      });

      // Reinitialize sliders if needed
      container.querySelectorAll('slider-component, .card-product-slider').forEach(slider => {
        if (slider.dispatchEvent) {
          slider.dispatchEvent(new CustomEvent('slider:update', { bubbles: true }));
        }
      });
    }
  }

  // Initialize
  if (!window.recentlyVisitedProductsRenderer) {
    window.recentlyVisitedProductsRenderer = new RecentlyVisitedProductsRenderer();
  }
})();

